import React from 'react';
import { useNavigate } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import Card from 'react-bootstrap/Card';
import { Col, Row, Spinner, Form, Button, Badge } from 'react-bootstrap';
import { connect } from "react-redux";
import { logout } from "../redux/reducers/user-actions";
import { useDispatch } from 'react-redux';

const Dashboard = ({ user }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Monthly analytics
    const now = new Date();
    const [selYear, setSelYear] = React.useState(now.getFullYear());
    const [selMonth, setSelMonth] = React.useState(now.getMonth() + 1);
    const [monthlySales, setMonthlySales] = React.useState(null);
    const [loadingMonthly, setLoadingMonthly] = React.useState(false);
    const [monthlyError, setMonthlyError] = React.useState(false);

    // Admin: test email
    const [testEmailAddr, setTestEmailAddr] = React.useState('');
    const [testEmailStatus, setTestEmailStatus] = React.useState(null);
    const [testEmailSending, setTestEmailSending] = React.useState(false);

    const fetchMonthly = React.useCallback(async (year, month) => {
        setLoadingMonthly(true);
        setMonthlyError(false);
        try {
            const authToken = localStorage.getItem('authToken');
            const isAdmin = user?.user_id === 1;
            const monthlySalesRoute = isAdmin ? '/api/admin/monthlysales' : '/api/creator/monthlysales';
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}${monthlySalesRoute}?year=${year}&month=${month}`,
                { headers: { Authorization: authToken } }
            );
            if (res.status === 401) { dispatch(logout()); navigate('/login'); return; }
            if (!res.ok) { setMonthlyError(true); return; }
            const data = await res.json();
            setMonthlySales(data);
        } catch {
            setMonthlyError(true);
        } finally {
            setLoadingMonthly(false);
        }
    }, [dispatch, navigate, user]);

    React.useEffect(() => {
        fetchMonthly(now.getFullYear(), now.getMonth() + 1);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleQueryMonthly = (e) => {
        e.preventDefault();
        fetchMonthly(selYear, selMonth);
    };

    // Year options: from 2024 up to current year
    const yearOptions = [];
    for (let y = 2024; y <= now.getFullYear(); y++) yearOptions.push(y);

    const monthNames = ['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'];

    return (
        <div className="container mt-5 mb-5">
            <h1 className="mb-4">Dashboard</h1>
            <h2 className="mb-4">歡迎回來 {user ? user.username : ''} !</h2>

            {/* ── Monthly analytics ── */}
            <h2 className="mb-3 mt-2">月份銷售報表</h2>
            <Form className="mb-3" onSubmit={handleQueryMonthly}>
                <Row className="align-items-end g-2">
                    <Col xs="auto">
                        <Form.Label className="mb-1">年份</Form.Label>
                        <Form.Select value={selYear} onChange={e => setSelYear(Number(e.target.value))} style={{ width: '110px' }}>
                            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs="auto">
                        <Form.Label className="mb-1">月份</Form.Label>
                        <Form.Select value={selMonth} onChange={e => setSelMonth(Number(e.target.value))} style={{ width: '100px' }}>
                            {monthNames.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                        </Form.Select>
                    </Col>
                    <Col xs="auto">
                        <Button variant="dark" type="submit" disabled={loadingMonthly}>
                            {loadingMonthly ? <Spinner animation="border" size="sm" /> : '查詢'}
                        </Button>
                    </Col>
                </Row>
            </Form>

            {loadingMonthly && (
                <div className="d-flex justify-content-center my-4">
                    <Spinner animation="border" />
                </div>
            )}

            {!loadingMonthly && monthlyError && (
                <p className="text-danger">載入失敗，請稍後再試。</p>
            )}

            {!loadingMonthly && monthlySales && !monthlyError && (
                <>
                    <Row className="mb-3 g-3">
                        <Col xs="auto">
                            <Card border="primary" style={{ minWidth: '180px' }}>
                                <Card.Body className="text-center">
                                    <Card.Title className="text-primary mb-0">
                                        {monthlySales.total_count}
                                    </Card.Title>
                                    <small className="text-muted">
                                        {monthlySales.year} 年 {monthlySales.month} 月・總銷售量
                                    </small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs="auto">
                            <Card border="success" style={{ minWidth: '180px' }}>
                                <Card.Body className="text-center">
                                    <Card.Title className="text-success mb-0">
                                        NT${monthlySales.total_revenue?.toLocaleString()}
                                    </Card.Title>
                                    <small className="text-muted">
                                        {monthlySales.year} 年 {monthlySales.month} 月・總收入
                                    </small>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {(!monthlySales.items || monthlySales.items.length === 0)
                        ? <p className="text-muted">本月無已完成訂單。</p>
                        : <Table striped bordered hover responsive>
                            <thead className="table-dark">
                                <tr>
                                    <th>#</th>
                                    <th>曲名</th>
                                    <th className="text-center">銷售量</th>
                                    <th className="text-end">收入 (NT$)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlySales.items.map((item, idx) => (
                                    <tr key={item.song_id}>
                                        <td>{idx + 1}</td>
                                        <td>
                                            {item.song_name}
                                            {idx === 0 && <Badge bg="warning" text="dark" className="ms-2">最佳</Badge>}
                                        </td>
                                        <td className="text-center">{item.count}</td>
                                        <td className="text-end">{item.revenue?.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="fw-bold">
                                    <td colSpan={2} className="text-end">合計</td>
                                    <td className="text-center">{monthlySales.total_count}</td>
                                    <td className="text-end">{monthlySales.total_revenue?.toLocaleString()}</td>
                                </tr>
                            </tfoot>
                          </Table>
                    }
                </>
            )}

            {/* ── Action buttons ── */}
            <div className="mt-4 mb-4">
                <Button variant="primary" className="me-2" onClick={() => navigate('/manage')}>歌曲管理</Button>
                <Button variant="primary" className="me-2" onClick={() => navigate('/withdraw')}>提領款項</Button>
                <Button variant="primary" onClick={() => navigate('/creatororder')}>訂單紀錄</Button>
            </div>

            {/* ── Admin: test email panel ── */}
            {user?.user_id === 1 && (
                <div className="mt-4 mb-5 p-4" style={{ border: '1px dashed #dc3545', borderRadius: '8px', background: '#fff8f8' }}>
                    <h5 className="text-danger mb-3">⚙ 管理員工具 — 測試電子郵件</h5>
                    <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                        輸入任意電子郵件地址，發送一封測試信以確認郵件服務正常運作。
                    </p>
                    <Row className="align-items-end g-2">
                        <Col xs={12} sm={7} md={5}>
                            <Form.Control
                                type="email"
                                placeholder="輸入收件人電子郵件"
                                value={testEmailAddr}
                                onChange={e => { setTestEmailAddr(e.target.value); setTestEmailStatus(null); }}
                            />
                        </Col>
                        <Col xs="auto">
                            <Button
                                variant="danger"
                                disabled={!testEmailAddr.trim() || testEmailSending}
                                onClick={async () => {
                                    setTestEmailSending(true);
                                    setTestEmailStatus(null);
                                    try {
                                        const res = await fetch(process.env.REACT_APP_API_URL + '/api/admin/testemail', {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': localStorage.getItem('authToken'),
                                            },
                                            body: JSON.stringify({ email: testEmailAddr }),
                                        });
                                        setTestEmailStatus(res.ok ? 'success' : 'error');
                                    } catch {
                                        setTestEmailStatus('error');
                                    } finally {
                                        setTestEmailSending(false);
                                    }
                                }}
                            >
                                {testEmailSending ? <Spinner animation="border" size="sm" /> : '發送測試信'}
                            </Button>
                        </Col>
                    </Row>
                    {testEmailStatus === 'success' && (
                        <p className="text-success mt-2 mb-0" style={{ fontSize: '0.875rem' }}>
                            ✓ 測試信已成功寄出至 {testEmailAddr}，請查收信箱。
                        </p>
                    )}
                    {testEmailStatus === 'error' && (
                        <p className="text-danger mt-2 mb-0" style={{ fontSize: '0.875rem' }}>
                            ✗ 寄送失敗，請確認郵件服務設定是否正常。
                        </p>
                    )}

                    <hr />
                    <p className="text-muted mb-2" style={{ fontSize: '0.875rem' }}>
                        若郵件服務授權過期，點擊下方按鈕重新授權 Gmail。授權完成後伺服器將自動更新 token，無需重啟。
                    </p>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={async () => {
                            try {
                                const res = await fetch(process.env.REACT_APP_API_URL + '/api/admin/gmailauthurl', {
                                    headers: { 'Authorization': localStorage.getItem('authToken') },
                                });
                                if (!res.ok) { alert('無法取得授權連結，請確認伺服器設定。'); return; }
                                const data = await res.json();
                                window.open(data.auth_url, '_blank');
                            } catch {
                                alert('發生錯誤，請稍後再試。');
                            }
                        }}
                    >
                        重新授權 Gmail
                    </Button>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({ user: state.user.user });
export default connect(mapStateToProps)(Dashboard);
